'use client';

import React, { useEffect, useState } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Button } from '@depop/web-ui-kit/Button';
import { ShieldIcon } from '@depop/web-ui-kit/Icons/ShieldIcon';
import { Modal } from '@depop/web-ui-kit/Modal';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { ExtendedLink } from '../ExtendedLink';

import styles from './styles.module.css';

import { getMfaNudgeStatus } from '@/modules/mfa/helpers/getMfaNudgeStatus';
import { setMfaNudgeStatus } from '@/modules/mfa/helpers/setMfaNudgeStatus';
import { useMfaStatus } from '@/modules/mfa/hooks/useMfaStatus';
import { useMFATracking } from '@/modules/mfa/useMFATracking';

function Content({
  onDismissClick,
  onCtaClick,
}: {
  onDismissClick: () => void;
  onCtaClick: () => void;
}) {
  const t = useTranslations('common');
  const pathname = usePathname();
  return (
    <div className={styles.container}>
      <ShieldIcon size={60} className={styles.shieldIcon} />
      <Heading variant="lg" className={styles.title}>
        {t('MFAPromptModal.Title')}
      </Heading>
      <Body variant="sm-regular" className={styles.textBody}>
        {t('MFAPromptModal.Body')}
      </Body>
      <ExtendedLink
        className={styles.cta}
        href={`/settings/mfa/enable/?redirect=${pathname}`}
        onClick={onCtaClick}
      >
        <Button as="span">{t('MFAPromptModal.CtaText')}</Button>
      </ExtendedLink>
      <button className={styles.dismissModalButton} onClick={onDismissClick}>
        <Body as="span" variant="sm-regular">
          {t('MFAPromptModal.RemindMeLater')}
        </Body>
      </button>
    </div>
  );
}

type Props = {
  flow: 'sellerhub' | 'profile';
};

export function MfaPromptModal({ flow }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldFetchMfaStatus, setShouldFetchMfaStatus] = useState(false);
  const {
    sendMfaAdoptionSplashView,
    sendMfaAdoptionSplashContinueAction,
    sendMfaAdoptionSplashCancelAction,
  } = useMFATracking();

  const {
    data: {
      mfa_enabled: isMfaEnabled,
      ever_enabled_mfa: hasEverEnabledMfa,
    } = {},
    isFetched,
  } = useMfaStatus(shouldFetchMfaStatus);

  useEffect(() => {
    const mfaNudgeStatus = getMfaNudgeStatus();

    if (mfaNudgeStatus) {
      setShouldFetchMfaStatus(true);
    }

    const shouldShowModal =
      mfaNudgeStatus && !isMfaEnabled && !hasEverEnabledMfa && isFetched;

    if (shouldShowModal) {
      sendMfaAdoptionSplashView(flow);
      setIsOpen(true);
    }
  }, [isMfaEnabled, isFetched, flow]);

  function handleDismissClick() {
    setIsOpen(false);
    sendMfaAdoptionSplashCancelAction();
    setMfaNudgeStatus();
  }

  function handleCloseModal() {
    sendMfaAdoptionSplashCancelAction();
    setMfaNudgeStatus();
  }

  function handleCtaClick() {
    sendMfaAdoptionSplashContinueAction();
  }

  return (
    <Modal
      content={() => (
        <Content
          onDismissClick={handleDismissClick}
          onCtaClick={handleCtaClick}
        />
      )}
      isOpen={isOpen}
      setOpen={setIsOpen}
      onCloseModal={handleCloseModal}
    />
  );
}
